import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
}

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
  children?: ReactNode;
  /** Fon ustiga bosganda yopilmasin (forma to'ldirilayotganda). */
  persistent?: boolean;
}

export function Modal({ open, onClose, title, description, footer, size = 'md', children, persistent }: ModalProps) {
  useEscape(open, onClose);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) panel.current?.focus();
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="kts-overlay" onMouseDown={(e) => !persistent && e.target === e.currentTarget && onClose()}>
      <div
        ref={panel}
        className={clsx('kts-modal', size !== 'md' && `kts-modal--${size}`)}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
      >
        {(title || description) && (
          <div className="kts-modal__header">
            <div style={{ flex: 1, minWidth: 0 }}>
              {title && <div className="kts-modal__title">{title}</div>}
              {description && <div className="kts-modal__desc">{description}</div>}
            </div>
            <Button variant="ghost" size="sm" iconOnly icon={<X />} aria-label="Yopish" onClick={onClose} />
          </div>
        )}
        <div className="kts-modal__body">{children}</div>
        {footer && <div className="kts-modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  children,
}: Omit<ModalProps, 'persistent' | 'size'> & { size?: 'md' | 'lg' }) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="kts-overlay kts-drawer-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className={clsx('kts-drawer', size === 'lg' && 'kts-drawer--lg')} role="dialog" aria-modal="true">
        <div className="kts-modal__header" style={{ borderBottom: '1px solid var(--line)', paddingBottom: 14 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && <div className="kts-modal__title">{title}</div>}
            {description && <div className="kts-modal__desc">{description}</div>}
          </div>
          <Button variant="ghost" size="sm" iconOnly icon={<X />} aria-label="Yopish" onClick={onClose} />
        </div>
        <div className="kts-modal__body" style={{ flex: 1, paddingTop: 18 }}>
          {children}
        </div>
        {footer && (
          <div className="kts-modal__footer" style={{ borderTop: '1px solid var(--line)', paddingTop: 14 }}>
            {footer}
          </div>
        )}
      </aside>
    </div>,
    document.body,
  );
}

/** Tasdiqlash oynasi: `await confirm()` uslubida ishlatish uchun `useConfirm`. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Tasdiqlash',
  cancelLabel = 'Bekor qilish',
  danger,
  loading,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}

export function useConfirm() {
  const [state, setState] = useState<{
    title: ReactNode;
    description?: ReactNode;
    danger?: boolean;
    confirmLabel?: string;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const confirm = (opts: { title: ReactNode; description?: ReactNode; danger?: boolean; confirmLabel?: string }) =>
    new Promise<boolean>((resolve) => setState({ ...opts, resolve }));
  const dialog = state ? (
    <ConfirmDialog
      open
      title={state.title}
      description={state.description}
      danger={state.danger}
      confirmLabel={state.confirmLabel}
      onConfirm={() => {
        state.resolve(true);
        setState(null);
      }}
      onClose={() => {
        state.resolve(false);
        setState(null);
      }}
    />
  ) : null;
  return { confirm, dialog };
}
